import { useMutation, useQueryClient } from '@tanstack/react-query'
import { requestJson } from '@/lib/api/client/request'
import type { ContractBodyInput } from '@/lib/api/contracts'
import {
  createWorkspaceCredentialContract,
  updateWorkspaceCredentialContract,
} from '@/lib/api/contracts/credentials'
import type { ResourceOwner } from '@/lib/core/resource-scope'
import { credentialWorkspaceId } from '@/hooks/queries/credentials'
import { oauthCredentialKeys } from '@/hooks/queries/oauth/oauth-credentials'
import { workspaceCredentialKeys } from '@/hooks/queries/utils/credential-keys'
import { invalidateSelectorQueries } from '@/hooks/queries/utils/selector-keys'

function useReconcileCredentials() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: workspaceCredentialKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: oauthCredentialKeys.lists() }),
      invalidateSelectorQueries(queryClient),
    ])
}
export function useCreateScopedCredential() {
  const reconcile = useReconcileCredentials()
  return useMutation({
    mutationFn: async ({
      workspaceId,
      organizationId,
      ...body
    }: Omit<ContractBodyInput<typeof createWorkspaceCredentialContract>, 'workspaceId'> &
      ResourceOwner) =>
      requestJson(createWorkspaceCredentialContract, {
        body: { ...body, workspaceId: credentialWorkspaceId({ workspaceId, organizationId }) },
      }),
    onSuccess: reconcile,
  })
}
export function useUpdateScopedCredential() {
  const reconcile = useReconcileCredentials()
  return useMutation({
    mutationFn: async ({
      credentialId,
      workspaceId,
      organizationId,
      ...body
    }: { credentialId: string } & ContractBodyInput<typeof updateWorkspaceCredentialContract> &
      ResourceOwner) =>
      requestJson(updateWorkspaceCredentialContract, {
        params: { id: credentialId },
        body,
        query: { workspaceId: credentialWorkspaceId({ workspaceId, organizationId }) },
      }),
    onSuccess: reconcile,
  })
}
